/**
 * PDF viewer with optional highlight overlays for cited evidence (Phase 5).
 * When quotes are provided, highlights are computed in the viewer from the PDF text
 * so the exact phrase is always matched (no pipeline re-run needed).
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import type { TextContent, TextItem } from "pdfjs-dist/types/src/display/api";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import "./PdfSourceViewer.css";

// Configure pdfjs worker: use jsDelivr (pdfjs-dist 4.x uses .mjs; cdnjs only has legacy pdf.js)
try {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
} catch {
  // ignore if already set
}

export interface EvidenceRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface EvidenceItem {
  document_id: number;
  page_number: number;
  quote: string;
  highlightable: boolean;
  rects: EvidenceRect[];
}

/** Normalize for matching: lowercase, collapse whitespace */
function normalizeText(t: string): string {
  return t.toLowerCase().replace(/\s+/g, " ").trim();
}

/** Build search string with punctuation/whitespace normalized and map from search index to fullText index */
function buildSearchWithMapping(fullText: string): { search: string; searchToOrig: number[] } {
  const searchToOrig: number[] = [];
  let search = "";
  for (let i = 0; i < fullText.length; i++) {
    const c = fullText[i];
    const lower = c.toLowerCase();
    if (/\s/.test(c)) {
      if (search.length > 0 && search[search.length - 1] !== " ") {
        search += " ";
        searchToOrig.push(i);
      }
    } else if (/[,.;:\-–—()'"\[\]\u2019]/.test(c)) {
      if (search.length > 0 && search[search.length - 1] !== " ") {
        search += " ";
        searchToOrig.push(i);
      }
    } else {
      search += lower;
      searchToOrig.push(i);
    }
  }
  const trimmed = search.trim();
  let skip = 0;
  while (skip < search.length && /\s/.test(search[skip])) skip++;
  return {
    search: trimmed,
    searchToOrig: searchToOrig.slice(skip, skip + trimmed.length),
  };
}

/** Normalize quote for search: same rules as buildSearchWithMapping output */
function normalizeQuoteForSearch(quote: string): string {
  return quote
    .replace(/[,.;:\-–—()'"\[\]\u2019]/g, " ")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Sort text items into reading order (top-to-bottom, left-to-right) so joined text is readable */
function sortItemsReadingOrder(items: TextItem[]): TextItem[] {
  return [...items].sort((a, b) => {
    const getY = (it: TextItem) => ("transform" in it && it.transform ? it.transform[5] : 0);
    const getX = (it: TextItem) => ("transform" in it && it.transform ? it.transform[4] : 0);
    const yA = getY(a);
    const yB = getY(b);
    if (Math.abs(yA - yB) > 2) return yB - yA;
    return getX(a) - getX(b);
  });
}

/** True if position in normalized string is at a word boundary (start or after space) */
function atWordStart(normFull: string, idx: number): boolean {
  return idx <= 0 || normFull[idx - 1] === " ";
}

/** One rect in PDF points (top-left coords). */
type PtRect = { x: number; y: number; width: number; height: number };

/** Build one highlight rect per line for items [startItem, endItem], so multi-line phrases get separate boxes per line (no double-height). */
function rectsForRangeByLine(
  items: TextItem[],
  startItem: number,
  endItem: number,
  pageWidthPts: number,
  pageHeightPts: number
): Array<{ x: number; y: number; width: number; height: number }> {
  if (startItem < 0 || endItem < 0 || startItem > endItem) return [];
  const itemRects: PtRect[] = [];
  for (let i = startItem; i <= endItem; i++) {
    const it = items[i];
    const tx = it.transform;
    const x = tx[4];
    const y = tx[5];
    const w = (it as { width?: number }).width ?? 0;
    const h = (it as { height?: number }).height ?? 0;
    const yTop = pageHeightPts - y - h;
    itemRects.push({ x, y: yTop, width: w, height: h });
  }
  const lineThreshold = 3;
  const lines: PtRect[][] = [];
  let currentLine: PtRect[] = [];
  let lastY = -1e9;
  for (const r of itemRects) {
    const midY = r.y + r.height / 2;
    if (currentLine.length > 0 && Math.abs(midY - lastY) > lineThreshold) {
      lines.push(currentLine);
      currentLine = [];
    }
    currentLine.push(r);
    lastY = midY;
  }
  if (currentLine.length > 0) lines.push(currentLine);

  const padX = 0.15;
  const padY = 0.4;
  const out: Array<{ x: number; y: number; width: number; height: number }> = [];
  for (const line of lines) {
    const minX = Math.min(...line.map((r) => r.x));
    const minY = Math.min(...line.map((r) => r.y));
    const maxX = Math.max(...line.map((r) => r.x + r.width));
    const maxY = Math.max(...line.map((r) => r.y + r.height));
    let x = (minX / pageWidthPts) * 100 - padX;
    let y = (minY / pageHeightPts) * 100 - padY;
    let w = ((maxX - minX) / pageWidthPts) * 100 + padX * 2;
    let h = ((maxY - minY) / pageHeightPts) * 100 + padY * 2;
    if (x < 0) {
      w += x;
      x = 0;
    }
    if (y < 0) {
      h += y;
      y = 0;
    }
    if (x + w > 100) w = 100 - x;
    if (y + h > 100) h = 100 - y;
    out.push({ x, y, width: w, height: h });
  }
  return out;
}

/** Find all start indices of `needle` in `normFull` that are at word boundaries */
function findAllAtWordBoundaries(normFull: string, needle: string): number[] {
  const indices: number[] = [];
  for (let from = 0; ; ) {
    const idx = normFull.indexOf(needle, from);
    if (idx === -1) break;
    if (atWordStart(normFull, idx)) indices.push(idx);
    from = idx + 1;
  }
  return indices;
}

/** Build fullText with space between items on different lines so phrases can match across lines; also return fullTextToItem[i] = item index for fullText[i]. */
function buildFullTextWithLineSpaces(
  items: TextItem[]
): { fullText: string; fullTextToItem: number[] } {
  const fullTextToItem: number[] = [];
  let fullText = "";
  const lineThreshold = 3;
  let prevY = -1e9;
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const y = "transform" in it && it.transform ? it.transform[5] : 0;
    if (i > 0 && Math.abs(y - prevY) > lineThreshold) {
      fullText += " ";
      fullTextToItem.push(-1);
    }
    for (let j = 0; j < it.str.length; j++) {
      fullText += it.str[j];
      fullTextToItem.push(i);
    }
    prevY = y;
  }
  return { fullText, fullTextToItem };
}

/** Find phrase in page text (punctuation-insensitive, word-boundary aware) and return rects per line for each occurrence */
function findPhraseRects(
  textContent: TextContent,
  pageWidthPts: number,
  pageHeightPts: number,
  quote: string
): Array<{ x: number; y: number; width: number; height: number }> {
  const rawItems = (textContent.items as TextItem[]).filter((i) => "str" in i && i.str);
  if (rawItems.length === 0) return [];
  const items = sortItemsReadingOrder(rawItems);
  const { fullText, fullTextToItem } = buildFullTextWithLineSpaces(items);
  const { search: normFull, searchToOrig } = buildSearchWithMapping(fullText);
  const normQuote = normalizeQuoteForSearch(quote);
  if (!normQuote) return [];

  let matchLen = normQuote.length;
  let indices = findAllAtWordBoundaries(normFull, normQuote);
  if (indices.length === 0) {
    const words = normQuote.split(" ").filter(Boolean);
    for (let n = Math.min(12, words.length); n >= 2; n--) {
      const prefix = words.slice(0, n).join(" ");
      indices = findAllAtWordBoundaries(normFull, prefix);
      if (indices.length > 0) {
        matchLen = prefix.length;
        break;
      }
    }
  }
  if (indices.length === 0) return [];

  const out: Array<{ x: number; y: number; width: number; height: number }> = [];
  for (const startIdx of indices) {
    const endIdx = startIdx + matchLen;
    const startChar = searchToOrig[startIdx];
    const endChar = endIdx <= searchToOrig.length ? searchToOrig[endIdx - 1] + 1 : fullText.length;
    let startItem = fullTextToItem[Math.min(startChar, fullTextToItem.length - 1)];
    let endItem = fullTextToItem[Math.min(endChar - 1, fullTextToItem.length - 1)];
    if (startItem < 0) {
      for (let k = startChar; k < fullTextToItem.length; k++) {
        if (fullTextToItem[k] >= 0) {
          startItem = fullTextToItem[k];
          break;
        }
      }
    }
    if (endItem < 0) {
      for (let k = endChar - 1; k >= 0; k--) {
        if (fullTextToItem[k] >= 0) {
          endItem = fullTextToItem[k];
          break;
        }
      }
    }
    if (startItem >= 0 && endItem >= 0) {
      const rects = rectsForRangeByLine(items, startItem, endItem, pageWidthPts, pageHeightPts);
      out.push(...rects);
    }
  }
  return out;
}

export interface PdfSourceViewerProps {
  /** PDF URL (blob URL or API URL with same-origin credentials) */
  pdfUrl: string | null;
  /** Current page to display (1-based) */
  pageNumber: number;
  /** Page dimensions in PDF points (for overlay %) */
  pageWidthPts: number;
  pageHeightPts: number;
  /** Highlight rects in PDF points (fallback when no quotes for client-side match) */
  rects: EvidenceRect[];
  /** Cited quote text (single) */
  quote: string;
  /** When set, highlights are computed in the viewer from PDF text to match exact phrase */
  quotes?: string[];
  /** Whether highlights could be anchored (backend) */
  highlightable: boolean;
  /** Total number of pages in the PDF (fallback until document loads) */
  numPages?: number;
  /** Callback when document loads with actual page count from the PDF file */
  onDocumentLoadSuccess?: (info: { numPages: number }) => void;
  /** Callback when user changes page */
  onPageChange?: (page: number) => void;
  /** Show page controls (prev/next) */
  showPageControls?: boolean;
  /** When true (e.g. viewing item PDF), highlights may not apply; show gentler message */
  isItemPdf?: boolean;
}

export default function PdfSourceViewer({
  pdfUrl,
  pageNumber,
  pageWidthPts,
  pageHeightPts,
  rects,
  quote,
  quotes,
  highlightable,
  numPages = 0,
  onDocumentLoadSuccess,
  onPageChange,
  showPageControls = true,
  isItemPdf = false,
}: PdfSourceViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  /** Client-side highlight rects (exact phrase match from PDF text); when set, we use these instead of backend rects */
  const [clientRects, setClientRects] = useState<Array<{ x: number; y: number; width: number; height: number }>>([]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setContainerWidth(w);
      }
    });
    ro.observe(el);
    setContainerWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const onGetTextSuccess = useCallback(
    (textContent: TextContent) => {
      const toMatch = quotes?.length ? quotes : quote ? [quote] : [];
      if (toMatch.length === 0 || pageWidthPts <= 0 || pageHeightPts <= 0) {
        setClientRects([]);
        return;
      }
      const withQuoteLen: Array<{ rect: { x: number; y: number; width: number; height: number }; quoteLen: number }> = [];
      for (const q of toMatch) {
        if (!q?.trim()) continue;
        const rectsForQuote = findPhraseRects(textContent, pageWidthPts, pageHeightPts, q);
        const len = q.trim().length;
        for (const rect of rectsForQuote) {
          withQuoteLen.push({ rect, quoteLen: len });
        }
      }
      const rectsOnly = withQuoteLen.map((w) => w.rect);
      const filtered = rectsOnly.filter((r) => {
        const areaR = r.width * r.height;
        const containedBySmaller = rectsOnly.some(
          (s) =>
            s !== r &&
            s.width * s.height < areaR &&
            s.x >= r.x &&
            s.y >= r.y &&
            s.x + s.width <= r.x + r.width &&
            s.y + s.height <= r.y + r.height
        );
        return !containedBySmaller;
      });
      setClientRects(filtered);
    },
    [quotes, quote, pageWidthPts, pageHeightPts]
  );

  useEffect(() => {
    setClientRects([]);
  }, [pageNumber, pdfUrl]);

  const hasQuotesToMatch = (quotes?.length ?? 0) > 0 || (quote?.trim()?.length ?? 0) > 0;
  const backendRectsPercent =
    highlightable && rects.length > 0 && pageWidthPts > 0 && pageHeightPts > 0
      ? rects.map((rect) => ({
          x: (rect.x / pageWidthPts) * 100,
          y: (rect.y / pageHeightPts) * 100,
          width: (rect.w / pageWidthPts) * 100,
          height: (rect.h / pageHeightPts) * 100,
        }))
      : [];
  // When we have cited phrases, highlight ONLY what we matched on the client (exact phrases).
  // Never use backend rects in that case—they are per-word and produce extra highlights.
  const highlightRects = hasQuotesToMatch
    ? clientRects
    : clientRects.length > 0
      ? clientRects
      : backendRectsPercent;

  if (!pdfUrl) {
    return (
      <div className="pdf-source-viewer pdf-source-viewer--loading">
        <p>Loading PDF...</p>
      </div>
    );
  }

  const pageWidth = containerWidth ?? 612;

  return (
    <div className="pdf-source-viewer" ref={containerRef}>
      <Document
        file={pdfUrl}
        onLoadSuccess={(pdf) => onDocumentLoadSuccess?.({ numPages: pdf?.numPages ?? 0 })}
        loading={
          <div className="pdf-source-viewer__loading">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span>Loading PDF...</span>
          </div>
        }
        error={
          <div className="pdf-source-viewer__error">
            <p>Failed to load PDF.</p>
          </div>
        }
      >
        <div className="pdf-source-viewer__page-wrap">
          <div className="pdf-source-viewer__page-inner">
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              className="pdf-source-viewer__page"
              onGetTextSuccess={onGetTextSuccess}
              onGetTextError={() => setClientRects([])}
            />
            {highlightRects.length > 0 && (
              <div
                className="pdf-source-viewer__overlay"
                aria-hidden
              >
                {highlightRects.map((rect, idx) => (
                  <div
                    key={idx}
                    className="pdf-source-viewer__highlight"
                    style={{
                      left: `${rect.x}%`,
                      top: `${rect.y}%`,
                      width: `${rect.width}%`,
                      height: `${rect.height}%`,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {showPageControls && numPages > 1 && onPageChange && (
          <div className="pdf-source-viewer__controls">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(1, pageNumber - 1))}
              disabled={pageNumber <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </Button>
            <span className="pdf-source-viewer__page-label">
              Page {pageNumber} of {numPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(numPages, pageNumber + 1))}
              disabled={pageNumber >= numPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </Document>

      {quote && (
        <div className="pdf-source-viewer__quote">
          <strong>Cited:</strong>
          <blockquote>{quote}</blockquote>
        </div>
      )}
      {!highlightable && quote && (
        <p className="pdf-source-viewer__warning">
          {isItemPdf
            ? "Citation shown below; highlights are for the full agenda when viewing the full document."
            : "Highlight unavailable — quote could not be matched to exact location."}
        </p>
      )}
    </div>
  );
}
